/**
 * Guardián del tablero de turno.
 *
 * SE APOYA EN `Protegida`, igual que `SoloAdministrador` y por la misma razón:
 * los cuatro desenlaces de sesión —comprobando, fallo, sin OAuth configurado,
 * sin registro— ya están resueltos ahí. Lo único que agrega este componente es
 * la comprobación de PERMISO.
 *
 * UN PERMISO, NO UNA LISTA DE ROLES (ADR-0007). Antes reproducía aquí la lista
 * `mostrador` + `administrador` que exigían las funciones de la migración 015.
 * Esa duplicación tenía fecha de caducidad: en cuanto `operar_mostrador` se
 * puede otorgar a un rol nuevo desde la pantalla de administración, la copia
 * en TypeScript deja de coincidir y alguien con permiso ve un «no puedes
 * entrar aquí» que el API contradice.
 *
 * Quien reserva NO tiene este permiso aunque sea el dueño de la reservación:
 * el punto del control de acceso es que alguien del mostrador constate la
 * llegada, no que la declare quien llega. Esa regla vive ahora en
 * `rol_permiso` y se puede reasignar sin desplegar.
 */

import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Estado } from '../componentes/Estado'
import type { UsuarioRegistrado } from '../api/perfil'
import { Protegida } from './Protegida'

/** El permiso que exigen `registrar_entrada`, `registrar_salida`, `devolver_llave` y `tablero_turno`. */
export const PERMISO_MOSTRADOR = 'operar_mostrador'

export function puedeAtenderMostrador(usuario: UsuarioRegistrado): boolean {
  return usuario.permisos.includes(PERMISO_MOSTRADOR)
}

interface PropiedadesSoloMostrador {
  children: (usuario: UsuarioRegistrado) => ReactNode
}

export function SoloMostrador({ children }: PropiedadesSoloMostrador) {
  return (
    <Protegida permite={['registrado']}>
      {(perfil) =>
        puedeAtenderMostrador(perfil.usuario) ? (
          <>{children(perfil.usuario)}</>
        ) : (
          <SinPermiso rol={perfil.usuario.rol} />
        )
      }
    </Protegida>
  )
}

/**
 * Lo que ve quien no atiende el mostrador. No es una pantalla de error: no hizo
 * nada mal, esta sección simplemente no es para su cuenta.
 */
function SinPermiso({ rol }: { rol: string }) {
  return (
    <Estado
      tono="aviso"
      titulo="Esta sección es para el mostrador."
      detalle={
        <>
          Aquí se registra quién llega, quién se va y qué llaves siguen fuera, y
          solo puede entrar una cuenta con el rol <code>mostrador</code> o{' '}
          <code>administrador</code>. La tuya tiene el rol <code>{rol}</code>.
          Los roles se asignan en el servidor, no desde la aplicación; si te
          toca atender el mostrador, pídelo a quien opera el sistema.
        </>
      }
      enVivo
    >
      <span className="acciones">
        <Link to="/" className="boton">
          Ir al inicio
        </Link>
        <Link to="/mis-reservaciones" className="boton">
          Ver mis reservaciones
        </Link>
      </span>
    </Estado>
  )
}
